import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Brain, CheckCircle, XCircle } from "lucide-react";

const COLORS = { primaryDark: "#08263e" };

const CATEGORY_LABELS = {
  saving_tips: "Saving Tips", budgeting: "Budgeting", debt_management: "Debt Management",
  business_tips: "Business Tips", company_overview: "Company Overview", pawning: "Pawning",
  loans: "Loans", microinsurance: "Microinsurance", banking: "Banking", remittance: "Remittance",
  bills_payment: "Bills Payment", "24k_rewards": "24K Rewards", money_exchange: "Money Exchange",
  ctpl_insurance: "CTPL Insurance", branch_locator: "Branch Locator"
};

export const SuggestionsPanel = ({
  suggestions, suggestionsGrouped, pendingSuggestionsCount, showSuggestions, setShowSuggestions,
  suggestionFilter, onFilterChange, onApprove, onDismiss, onDismissAll,
  approvingId, reassignCategory, setReassignCategory, learnedKeywords
}) => {
  if (pendingSuggestionsCount === 0 && !showSuggestions) return null;

  return (
    <Card id="faq-suggestions-panel" className="mb-6 border-amber-200" data-testid="faq-suggestions-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Brain size={18} className="text-amber-500" />
            FAQ Learning Suggestions
            {pendingSuggestionsCount > 0 && (
              <span className="bg-amber-100 text-amber-700 text-xs font-bold px-2 py-0.5 rounded-full" data-testid="suggestions-badge">
                {pendingSuggestionsCount} pending
              </span>
            )}
          </CardTitle>
          <div className="flex items-center gap-2">
            <select value={suggestionFilter} onChange={(e) => onFilterChange(e.target.value)} className="px-2 py-1 border rounded text-sm" data-testid="suggestions-filter">
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="dismissed">Dismissed</option>
              <option value="all">All</option>
            </select>
            <Button variant="ghost" size="sm" onClick={() => setShowSuggestions(!showSuggestions)}>
              {showSuggestions ? "Collapse" : "Expand"}
            </Button>
          </div>
        </div>
        <p className="text-xs text-[#888] mt-1">Queries that went to the LLM but were close to an FAQ category. Approve to teach the bot.</p>
      </CardHeader>
      {showSuggestions && (
        <CardContent>
          {suggestions.length === 0 ? (
            <p className="text-sm text-[#999] text-center py-4">
              {suggestionFilter === "pending" ? "No pending suggestions. The bot is matching well!" : `No ${suggestionFilter} suggestions.`}
            </p>
          ) : (
            <div className="space-y-2">
              {Object.entries(suggestionsGrouped).map(([category, items]) => (
                <div key={category} className="border rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm font-semibold capitalize" style={{ color: COLORS.primaryDark }}>{category.replace(/_/g, " ")}</span>
                    <span className="text-xs text-[#999]">{items.length} suggestion{items.length !== 1 ? "s" : ""}</span>
                    {suggestionFilter === "pending" && items.length > 1 && (
                      <Button variant="ghost" size="sm" className="ml-auto text-xs text-red-400 hover:bg-red-50 h-6 px-2" onClick={() => onDismissAll(category, items)} data-testid={`dismiss-all-${category}`}>
                        <XCircle size={12} className="mr-1" /> Dismiss all
                      </Button>
                    )}
                  </div>
                  <div className="space-y-2">
                    {items.map((s) => (
                      <div key={s.user_message} className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">&ldquo;{s.user_message}&rdquo;</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-xs text-[#999]">Score: {(s.similarity_score * 100).toFixed(1)}%</span>
                            <span className="text-xs text-[#999]">Hits: {s.hit_count || 1}</span>
                            {s.status === "approved" && <span className="text-xs text-green-600 font-medium">Approved</span>}
                            {s.status === "dismissed" && <span className="text-xs text-red-400 font-medium">Dismissed</span>}
                          </div>
                        </div>
                        {s.status === "pending" && (
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <select
                              className="text-xs border rounded px-1 py-1 max-w-[100px]"
                              value={reassignCategory[s.user_message] || category}
                              onChange={(e) => setReassignCategory(prev => ({ ...prev, [s.user_message]: e.target.value }))}
                            >
                              {[...new Set([category, ...Object.keys(CATEGORY_LABELS)])].map(c => (
                                <option key={c} value={c}>{c.replace(/_/g, " ")}</option>
                              ))}
                            </select>
                            <Button variant="ghost" size="sm" className="text-green-600 hover:bg-green-50 h-7 w-7 p-0" disabled={approvingId === s.user_message} onClick={() => onApprove(s)} title="Approve & add to FAQ">
                              <CheckCircle size={16} />
                            </Button>
                            <Button variant="ghost" size="sm" className="text-red-400 hover:bg-red-50 h-7 w-7 p-0" onClick={() => onDismiss(s.user_message)} title="Dismiss">
                              <XCircle size={16} />
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
          {learnedKeywords > 0 && (
            <div className="mt-3 pt-3 border-t flex items-center gap-2 text-xs text-[#666]">
              <Brain size={14} className="text-green-600" />
              <span><strong>{learnedKeywords}</strong> learned keyword{learnedKeywords !== 1 ? "s" : ""} active</span>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
};
